import React, { useState, useEffect } from 'react';
import { IconGauge, IconChartLine, IconCpu, IconCloudComputing, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { Badge } from '../../../ui/badge';
import { Spinner } from '../../../ui/spinner';
import { Progress } from '../../../ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../../../ui/tooltip';
import { cn } from '../../../../lib/utils';
import { useReviewAPI } from '../hooks/useReviewAPI';

/**
 * Performance Monitor Component
 * Displays real-time performance metrics for the review system
 */
const PerformanceMonitor = ({ compact = true, headerMode = false }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [error, setError] = useState(null);

  const api = useReviewAPI();

  // Load performance metrics once
  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPerformanceMetrics('1h');
      setMetrics(data);
    } catch (error) {
      console.error('Error loading performance metrics:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Format numbers for display
  const formatNumber = (num) => {
    if (!num && num !== 0) return 'N/A';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Get badge variant based on performance
  const getPerformanceVariant = (value, thresholds) => {
    if (value <= thresholds.good) return 'success';
    if (value <= thresholds.warning) return 'warning';
    return 'destructive';
  };

  // Header mode - inline metrics display
  if (headerMode) {
    if (loading && !metrics) return null;
    if (error || !metrics) return null;

    const cacheHitRate = metrics?.cache?.hits && metrics?.cache?.misses
      ? ((metrics.cache.hits / (metrics.cache.hits + metrics.cache.misses)) * 100).toFixed(0)
      : 0;

    const avgResponseTime = metrics?.performance?.averageResponseTime || 0;

    return (
      <TooltipProvider>
        <div className="flex items-center gap-3 px-4 py-1 bg-surface-1 border border-glass-border">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <IconCpu size={14} className="text-text-secondary" />
                <span className="text-xs font-medium text-text-primary">{cacheHitRate}%</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Cache Hit Rate</TooltipContent>
          </Tooltip>

          <div className="w-px h-4 bg-glass-border" />

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <IconGauge size={14} className="text-text-secondary" />
                <span className="text-xs font-medium text-text-primary">{avgResponseTime}ms</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Avg Response Time</TooltipContent>
          </Tooltip>

          <div className="w-px h-4 bg-glass-border" />

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <IconCloudComputing size={14} className="text-text-secondary" />
                <span className="text-xs font-medium text-text-primary">{formatNumber(metrics?.apiCalls?.twitter || 0)}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Twitter API Calls</TooltipContent>
          </Tooltip>

          <div className="w-px h-4 bg-glass-border" />

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <IconChartLine size={14} className="text-text-secondary" />
                <span className="text-xs font-medium text-text-primary">{metrics?.sessions?.active || 0}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Active Sessions</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  }

  // Original card mode
  if (loading && !metrics) {
    return (
      <div className="glass-card mb-2 p-2">
        <div className="flex items-center gap-2">
          <IconGauge size={16} className="text-accent" />
          <span className="text-xs text-text-secondary">Loading metrics...</span>
          <Progress value={50} className="flex-1 h-0.5" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card mb-2 p-2">
        <div className="flex items-center gap-2">
          <IconGauge size={16} className="text-error" />
          <span className="text-xs text-error">Metrics unavailable</span>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const cacheHitRate = parseFloat(metrics.cache?.hitRate || 0);
  const avgResponseTime = parseInt(metrics.performance?.avgResponseTime || 0);
  const twitterApiCalls = parseInt(metrics.apiCalls?.twitter || 0);

  return (
    <div className="glass-card mb-2">
      <div className="py-2 px-3">
        {/* Compact View */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconGauge size={16} className="text-accent" />
            <span className="text-xs font-semibold text-text-primary">Performance</span>

            {/* Quick metrics */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant={getPerformanceVariant(cacheHitRate, { good: 80, warning: 60 })} className="h-[18px] text-[0.6rem]">
                    {cacheHitRate}%
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Cache hit rate: {cacheHitRate}%</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant={getPerformanceVariant(avgResponseTime, { good: 200, warning: 500 })} className="h-[18px] text-[0.6rem]">
                    {avgResponseTime}ms
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Average response time: {avgResponseTime}ms</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant={getPerformanceVariant(twitterApiCalls, { good: 50, warning: 100 })} className="h-[18px] text-[0.6rem]">
                    API: {formatNumber(twitterApiCalls)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Twitter API calls: {twitterApiCalls}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {compact && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-0.5 text-text-secondary hover:text-text-primary"
            >
              {expanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
            </button>
          )}
        </div>

        {/* Expanded View */}
        {expanded && (
          <div className="mt-2">
            <div className="grid grid-cols-2 gap-2">
              {/* Cache Performance */}
              <div>
                <div className="flex items-center gap-1">
                  <IconCpu size={12} className="text-text-secondary" />
                  <span className="text-xs text-text-secondary">Cache</span>
                </div>
                <span className="text-xs font-semibold text-text-primary">
                  {metrics.cache?.hits || 0} hits / {metrics.cache?.misses || 0} misses
                </span>
              </div>

              {/* API Calls */}
              <div>
                <div className="flex items-center gap-1">
                  <IconCloudComputing size={12} className="text-text-secondary" />
                  <span className="text-xs text-text-secondary">API Calls</span>
                </div>
                <span className="text-xs font-semibold text-text-primary">
                  Total: {formatNumber(metrics.apiCalls?.totalRequests || 0)}
                </span>
              </div>

              {/* Sessions */}
              <div>
                <div className="flex items-center gap-1">
                  <IconChartLine size={12} className="text-text-secondary" />
                  <span className="text-xs text-text-secondary">Sessions</span>
                </div>
                <span className="text-xs font-semibold text-text-primary">
                  Active: {metrics.sessions?.active || 0} / Completed: {metrics.sessions?.completed || 0}
                </span>
              </div>

              {/* Performance Issues */}
              <div>
                <div className="flex items-center gap-1">
                  <IconGauge size={12} className="text-text-secondary" />
                  <span className="text-xs text-text-secondary">Issues</span>
                </div>
                <span className="text-xs font-semibold text-text-primary">
                  Slow queries: {metrics.performance?.slowQueries || 0}
                </span>
              </div>
            </div>

            {/* Last updated */}
            <div className="mt-1 text-right">
              <span className="text-[0.6rem] text-text-secondary">
                Updated: {new Date(metrics.timestamp || Date.now()).toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceMonitor;
